import { useCallback } from 'react';

import { getPaymentMethodIcon } from '@garnish/commerce';
import { capitalizeFirstLetter } from '@garnish/forms';
import { type StripePaymentElementChangeEvent } from '@stripe/stripe-js';

import { fallbackToSavedPaymentMethod } from '../../../../utils';
import { useNotifyStripeError } from '../../../useNotifyStripeError';

import type { PaymentMethodChangeHandler } from './types';

/**
 * Handles changes to the payment method, setting state as needed.
 */
export const usePaymentMethodChangeHandler = (params: PaymentMethodChangeHandler) => {
  const {
    isPaymentAlreadySavedRef,
    selectedPaymentMethodIdRef,
    paymentMethods,
    convertTypeToLabelAndIcon,
    createConfirmationToken,
    setIsAddingNewPaymentMethod,
    setSelectedPaymentMethod,
    setSelectedPaymentMethodType,
    setSelectedPaymentIcon,
  } = params;

  const notifyStripeError = useNotifyStripeError();

  return useCallback(
    async (event: StripePaymentElementChangeEvent) => {
      try {
        const paymentMethodId = event.value.payment_method?.id ?? '';

        selectedPaymentMethodIdRef.current = paymentMethodId;
        isPaymentAlreadySavedRef.current = Boolean(paymentMethodId);
        setSelectedPaymentMethodType(event.value.type);

        // Customer is selecting a new payment method.
        if (event.elementType === 'payment' && !event.complete) {
          setIsAddingNewPaymentMethod(true);
          setSelectedPaymentMethod('');
          setSelectedPaymentIcon('IconCreditCard');
          return;
        }

        // Customer selected a card that is already saved in Stripe.
        if (event.value.type === 'card' && paymentMethodId) {
          const savedCard = paymentMethods.find((pm) => pm.id === paymentMethodId);

          setIsAddingNewPaymentMethod(false);
          setSelectedPaymentMethod(savedCard?.description || 'Payment method');
          setSelectedPaymentIcon(getPaymentMethodIcon(savedCard?.cardType));

          return;
        }

        // Customer has added a new card.
        if (event.value.type === 'card' && event.complete) {
          const confirmationToken = await createConfirmationToken(true);
          const paymentMethodPreview = confirmationToken.payment_method_preview;

          if (!paymentMethodPreview?.card) return;

          const { brand, last4 } = paymentMethodPreview.card;

          const label = `${capitalizeFirstLetter(brand)} ${last4}`;

          setIsAddingNewPaymentMethod(true);
          setSelectedPaymentMethod(label);
          setSelectedPaymentIcon(getPaymentMethodIcon(brand.toUpperCase()));

          return;
        }

        // Customer selected a wallet payment type.
        if (event.value.type && event.complete) {
          const { label, icon } = convertTypeToLabelAndIcon(event.value.type);
          setIsAddingNewPaymentMethod(false);
          setSelectedPaymentMethod(label);
          setSelectedPaymentIcon(icon);
        }
      } catch (error) {
        notifyStripeError((error as Error).message);

        // Only the new-card path has a saved-card fallback; a wallet selection must survive.
        if (event.value.type !== 'card') return;

        const fallback = fallbackToSavedPaymentMethod(paymentMethods);
        const fallbackDescription = fallback?.description ?? '';
        const fallbackCardType = fallback?.cardType;
        setIsAddingNewPaymentMethod(false);
        setSelectedPaymentMethod(fallbackDescription);
        setSelectedPaymentIcon(getPaymentMethodIcon(fallbackCardType));
      }
    },
    [
      isPaymentAlreadySavedRef,
      selectedPaymentMethodIdRef,
      paymentMethods,
      setIsAddingNewPaymentMethod,
      setSelectedPaymentMethod,
      setSelectedPaymentMethodType,
      setSelectedPaymentIcon,
      createConfirmationToken,
      convertTypeToLabelAndIcon,
      notifyStripeError,
    ],
  );
};
